/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DeviceModelsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Device Models module.
----------------------------------------------------------------*/

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Edit, Trash2, Copy } from 'lucide-react';
import { useDeviceModelsData } from './hooks/useDeviceModelsData';
import * as api from './api';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import Combobox from '../../components/ui/Combobox';
import { DeviceModel } from './api';

export default function DeviceModelsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { models, vendors, loading, refresh } = useDeviceModelsData();
    const { showSuccess, showError } = useNotifications();

    // Grid State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVendorId, setSelectedVendorId] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<string>('model');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; modelId: number | string | null }>({
        isOpen: false,
        modelId: null
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

    // Filter and Sort Data
    const processedData = useMemo(() => {
        let filtered = [...models];

        // Filter by Vendor
        if (selectedVendorId && selectedVendorId !== 'all') {
            const vendorId = parseInt(selectedVendorId);
            filtered = filtered.filter(m => m.vendorId === vendorId);
        }

        // Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(m =>
                m.model.toLowerCase().includes(q) ||
                m.vendor?.name.toLowerCase().includes(q)
            );
        }

        // Sort
        filtered.sort((a, b) => {
            let aValue: unknown = a[sortBy as keyof typeof a];
            let bValue: unknown = b[sortBy as keyof typeof b];

            // Handle nested properties
            if (sortBy === 'vendor.name') {
                aValue = a.vendor?.name || '';
                bValue = b.vendor?.name || '';
            }

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        // Pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

        return { data: paginatedData, totalItems, totalPages, filteredData: filtered };
    }, [models, searchQuery, selectedVendorId, sortBy, sortOrder, currentPage, itemsPerPage]);

    // Check if columns have data to determine visibility
    const columnHasData = useMemo(() => {
        const data = processedData.filteredData;
        if (!data || data.length === 0) return {
            accounts: false,
            dssKeys: false,
            maxExpansionUnits: false,
            remotePhonebooks: false
        };

        return {
            accounts: data.some(m => ((m.accounts as { count?: number } | undefined)?.count ?? 0) > 0),
            dssKeys: data.some(m => ((m.keys as { dss_keys?: number } | undefined)?.dss_keys ?? 0) > 0),
            maxExpansionUnits: data.some(m => ((m.expansionModuleKeys as { count?: number } | undefined)?.count ?? 0) > 0),
            remotePhonebooks: data.some(m => ((m.phonebook as { remote_phonebooks?: { count?: number } } | undefined)?.remote_phonebooks?.count ?? 0) > 0),
        };
    }, [processedData.filteredData]);

    const handleDelete = async () => {
        if (!deleteModal.modelId) return;
        setIsDeleting(true);
        try {
            await api.deleteDeviceModel(deleteModal.modelId);
            showSuccess('Model deleted successfully');
            setDeleteModal({ isOpen: false, modelId: null });
            refresh();
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to delete model');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDuplicate = async (id: number) => {
        try {
            const newModel = await api.duplicateDeviceModel(id);
            showSuccess(t('common.modelDuplicated', 'Model duplicated successfully'));
            navigate(`${newModel.uuid ?? newModel.id}`);
        } catch (err) {
            showError(err instanceof Error ? err.message : t('common.duplicateError', 'Failed to duplicate model'));
        }
    };

    // Handle column toggle
    const handleColumnToggle = (key: string) => {
        setColumnVisibility(prev => ({
            ...prev,
            [key]: !(prev[key] ?? true)
        }));
    };

    // Apply column visibility
    const columnsDefinition = useMemo(() => [
        {
            key: 'vendor.name',
            label: 'Vendor',
            sortable: true,
            visible: true,
            render: (_: unknown, row: DeviceModel) => <span className="font-medium text-gray-900 dark:text-white">{row.vendor?.name}</span>
        },
        {
            key: 'model',
            label: 'Model',
            sortable: true,
            visible: true,
            render: (value: string, row: DeviceModel) => <span className="text-gray-700 dark:text-gray-300">{value}</span>
        },
        {
            key: 'isSystem',
            label: 'Type',
            sortable: true,
            visible: true,
            render: (value: boolean, row: DeviceModel) => (
                row.isSystem ? (
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/30">
                        System
                    </span>
                ) : (
                    <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/30">
                        Custom
                    </span>
                )
            )
        },
        {
            key: 'accounts',
            label: 'Accounts',
            sortable: true,
            visible: columnHasData.accounts,
            render: (_: unknown, row: DeviceModel) => <span className="text-center block text-gray-500 dark:text-gray-400">{(row.accounts as Record<string,number>|undefined)?.count || 0}</span>
        },
        {
            key: 'dssKeys',
            label: 'DSS Keys',
            sortable: true,
            visible: columnHasData.dssKeys,
            render: (_value: unknown, row: DeviceModel) => <span className="text-center block text-gray-500 dark:text-gray-400">{(row.keys as { dss_keys?: number } | undefined)?.dss_keys ?? 0}</span>
        },
        {
            key: 'maxExpansionUnits',
            label: 'Exp. Units',
            sortable: true,
            visible: columnHasData.maxExpansionUnits,
            render: (_value: unknown, row: DeviceModel) => {
                const expCount = (row.expansionModuleKeys as { count?: number } | undefined)?.count;
                return <span className="text-center block text-gray-500 dark:text-gray-400">{expCount ?? 0}</span>;
            }
        },
        {
            key: 'remotePhonebooks',
            label: 'Remote Books',
            sortable: false,
            visible: columnHasData.remotePhonebooks,
            render: (_: unknown, row: DeviceModel) => {
                const count = (row.phonebook as { remote_phonebooks?: { count?: number } } | undefined)?.remote_phonebooks?.count;
                return <span className="text-center block text-gray-500 dark:text-gray-400">{count ?? 0}</span>;
            }
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            sortable: false,
            visible: true
        }
    ], [t, columnHasData]);

    // Apply column visibility
    const columns = useMemo(() => columnsDefinition.map(col => ({
        ...col,
        visible: columnVisibility[col.key] ?? col.visible
    })), [columnsDefinition, columnVisibility]);

    const renderActions = (model: DeviceModel) => (
        <div className="flex items-center justify-end gap-2">
            <SimpleTooltip content={t('common.duplicate', 'Duplicate')} position="top">
                <button
                    onClick={() => handleDuplicate(model.id)}
                    className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md transition-colors text-green-600 dark:text-green-400"
                >
                    <Copy className="h-4 w-4" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.edit', 'Edit')} position="top">
                <button
                    onClick={() => navigate(`${model.uuid ?? model.id}`)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400"
                >
                    <Edit className="h-4 w-4" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={model.isSystem ? 'System models cannot be deleted' : t('common.delete', 'Delete')} position="top">
                <button
                    onClick={() => !model.isSystem && setDeleteModal({ isOpen: true, modelId: model.id })}
                    disabled={model.isSystem}
                    className={`p-2 rounded-md transition-colors ${model.isSystem
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400'
                        }`}
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </SimpleTooltip>
        </div>
    );

    const handleVendorToggle = (selectedId: string) => {
        if (selectedVendorId === selectedId) {
            setSelectedVendorId('all'); // Deselect if clicking same
        } else {
            setSelectedVendorId(selectedId);
        }
        setCurrentPage(1);
    };

    const vendorOptions = (vendors || []).map(vendor => ({
        value: vendor.id.toString(),
        label: vendor.name,
        checked: selectedVendorId === vendor.id.toString()
    }));

    const vendorFilter = (
        <div className="w-56">
            <Combobox
                options={vendorOptions}
                onToggle={handleVendorToggle}
                placeholder={t('common.filterByVendor', 'Filter by Vendor')}
                single={true}
                translations={{
                    searchOptions: t('common.searchVendors', 'Search vendors...'),
                    noOptionsFound: t('common.noVendorsFound', 'No vendors found'),
                    allSelected: t('common.allVendors', 'All Vendors'),
                    selected: t('common.selected', 'Selected'),
                    selectAll: t('common.selectAll', 'Select All'),
                    clearAll: t('common.clearAll', 'Clear All')
                }}
            />
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            <DataGridSimple
                title={t('common.deviceModels', 'Device Models')}
                titleIcon={<Smartphone className="h-5 w-5 text-primary" />}
                data={processedData.data}
                columns={columns}
                loading={loading}
                renderActions={renderActions}
                addButtonText={t('common.add', 'Add')}
                onAdd={() => navigate('new')}
                onColumnToggle={handleColumnToggle}
                filters={vendorFilter}

                // Search & Sort & Pagination Props
                searchQuery={searchQuery}
                onSearch={setSearchQuery}
                searchPlaceholder={t('common.searchModels', "Search models...")}

                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(column) => {
                    if (sortBy === column) {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                        setSortBy(column);
                        setSortOrder('asc');
                    }
                }}

                pagination={{
                    page: currentPage,
                    limit: itemsPerPage,
                    total: processedData.totalItems,
                    totalPages: processedData.totalPages
                }}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, modelId: null })}
                onConfirm={handleDelete}
                title={t('common.deleteItem', 'Delete Model')}
                message={t('common.confirmDelete', 'Are you sure you want to delete this device model?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
                isLoading={isDeleting}
            />
        </div>
    );
}
